import { useState } from 'react';
import { useUpdateCheckinOrderMutation } from '@websky/graphql';
import { batch, useDispatch, useSelector } from 'react-redux';
import { getCheckinOrder } from '../../../../store/order/selectors';
import { getUnconfirmedServices } from './utils';
import { fillCheckinOrder } from '../../../../store/order/actions';
import { UnconfirmedServicesMap } from './types';
import { UnconfirmedSeatsError } from '../../CheckinErrorBoundary/CheckinErrors';
import { captureErrorSentry } from '../../../../../../utils';
import { removeService, saveServices } from '../../../../store/selectedServices/actions';
import { ModifiedService } from '../../../../types';
import { getIsServicesSaving } from '../../../../store/servicesLoading/selectors';

const pollingIntervalInMs = 1000;
const pollingCountThreshold = 10;

export const useUnconfirmedServicesModal = () => {
	const order = useSelector(getCheckinOrder);
	const isServicesSaving = useSelector(getIsServicesSaving);

	const dispatch = useDispatch();

	const [unconfirmedServices, setUnconfirmedServices] = useState<UnconfirmedServicesMap>();

	const [updateCheckinOrder] = useUpdateCheckinOrderMutation({
		variables: {
			id: order.id
		}
	});

	const polling = async (callback: () => void, attempt: number) => {
		if (attempt === pollingCountThreshold) {
			const error = new UnconfirmedSeatsError(
				`updateCheckinOrder polling reach ${pollingCountThreshold} attempts`
			);
			captureErrorSentry(`${error.name}: ${error.message}`);
		}

		try {
			const result = await updateCheckinOrder();
			const updatedCheckinOrder = result?.data?.UpdateCheckinOrder;

			if (updatedCheckinOrder) {
				const hasUnconfirmedProduct = getUnconfirmedServices(updatedCheckinOrder).size > 0;

				if (hasUnconfirmedProduct) {
					setTimeout(() => polling(callback, attempt + 1), pollingIntervalInMs);
				} else {
					dispatch(fillCheckinOrder(updatedCheckinOrder));
					setUnconfirmedServices(null);
					callback();
				}
			}
		} catch (error) {
			console.error(error);
			setTimeout(() => polling(callback, attempt + 1), pollingIntervalInMs);
		}
	};

	const startPolling = (callback: () => void) => {
		setTimeout(() => polling(callback, 1), pollingIntervalInMs);
	};

	const removeServices = (services: ModifiedService[], callback: () => void) => {
		batch(() => {
			services.forEach(service => {
				dispatch(removeService(service));
			});

			dispatch(
				saveServices(undefined, {
					resolve: callback
				})
			);
		});
	};

	return {
		startPolling,
		unconfirmedServices,
		setUnconfirmedServices,
		removeServices,
		isServicesSaving
	};
};
